import React, { useMemo } from 'react';
import { BarChart, Bar, Cell, ResponsiveContainer, Rectangle, Tooltip, XAxis, YAxis } from 'recharts';
import { CATEGORY_COLORS, CATEGORY_LABELS, CATEGORY_ORDER } from '../../data/analyzerRules';

const SEVERITY_COLORS = {
  Error: '#ef4444',
  Warning: '#f59e0b',
  Info: '#3b82f6'
};

const toSeverityData = (severityCounts) => (
  Object.entries(severityCounts)
    .filter(([, value]) => value > 0)
    .map(([severity, value]) => ({ name: severity, value, color: SEVERITY_COLORS[severity] || '#94a3b8' }))
    .sort((a, b) => b.value - a.value)
);

const toCategoryData = (categoryCounts) => (
  CATEGORY_ORDER
    .filter((category) => categoryCounts[category])
    .map((category) => ({
      key: category,
      name: CATEGORY_LABELS[category] || 'Other',
      value: categoryCounts[category],
      color: CATEGORY_COLORS[category] || CATEGORY_COLORS.OTHER
    }))
    .sort((a, b) => b.value - a.value)
);

const ActiveBarShape = (props) => (
  <Rectangle {...props} stroke="none" strokeWidth={0} />
);

const BaseBarShape = (props) => (
  <Rectangle {...props} stroke="none" strokeWidth={0} />
);

const AnalyzerSummaryBar = ({
  severityCounts,
  categoryCounts,
  totalIssues,
  activeSeverities,
  onToggleSeverity,
  activeCategories,
  onToggleCategory
}) => {
  const severityData = toSeverityData(severityCounts);
  const categoryData = toCategoryData(categoryCounts);

  const getSegmentOpacity = (activeList, key) => {
    if (activeList.length === 0) {
      return 1;
    }
    return activeList.includes(key) ? 1 : 0.35;
  };
  const categoryLabelMap = useMemo(() => (
    categoryData.reduce((acc, entry) => {
      acc[entry.key] = entry.name;
      return acc;
    }, {})
  ), [categoryData]);
  const severityBarData = useMemo(() => ([
    severityData.reduce((acc, entry) => {
      acc[entry.name] = entry.value;
      return acc;
    }, { name: 'Severity' })
  ]), [severityData]);
  const categoryBarData = useMemo(() => ([
    categoryData.reduce((acc, entry) => {
      acc[entry.key] = entry.value;
      return acc;
    }, { name: 'Category' })
  ]), [categoryData]);

  return (
    <div className="rounded-xl p-4" style={{border:'1px solid var(--tqa-border-dark)',background:'var(--tqa-surface)'}}>
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_1fr] gap-4 items-start">
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>Total issues</div>
          <div className="text-3xl font-semibold" style={{color:'rgba(255,255,255,0.9)'}}>{totalIssues}</div>
          <div className="text-[11px]" style={{color:'rgba(255,255,255,0.35)'}}>Click bars or labels to filter</div>
        </div>

        <div className="pl-4" style={{borderLeft:'1px solid var(--tqa-border-dark)'}}>
          <div className="text-xs uppercase tracking-wide mb-2" style={{color:'rgba(255,255,255,0.45)'}}>Severity</div>
          <div className="h-10">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={severityBarData}
                layout="vertical"
                margin={{ left: 0, right: 0 }}
                className="outline-none"
                style={{ outline: 'none' }}
                tabIndex={-1}
              > 
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  formatter={(value, name) => [value, name]}
                  shared={false}
                  contentStyle={{ backgroundColor: 'var(--tqa-black)', borderColor: 'var(--tqa-border-dark)' }}
                  itemStyle={{ color: 'var(--tqa-white)' }} labelStyle={{ color: 'var(--tqa-timberwolf)' }}
                />
                {severityData.map((entry) => (
                  <Bar
                    key={entry.name}
                    dataKey={entry.name}
                    stackId="severity"
                    radius={0}
                    fill={entry.color}
                    stroke="none"
                    strokeWidth={0}
                    shape={<BaseBarShape />}
                    activeBar={<ActiveBarShape />}
                    fillOpacity={getSegmentOpacity(activeSeverities, entry.name)}
                    onClick={() => onToggleSeverity(entry.name)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]" style={{color:'rgba(255,255,255,0.45)'}}>
            {severityData.map((entry) => (
              <button
                key={entry.name}
                type="button"
                onClick={() => onToggleSeverity(entry.name)}
                className="inline-flex items-center gap-1"
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
                {entry.name}
              </button>
            ))}
          </div>
        </div>

        <div className="pl-4" style={{borderLeft:'1px solid var(--tqa-border-dark)'}}>
          <div className="text-xs uppercase tracking-wide mb-2" style={{color:'rgba(255,255,255,0.45)'}}>Category distribution</div>
          <div className="h-10">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={categoryBarData}
                layout="vertical"
                margin={{ left: 0, right: 0 }}
                className="outline-none"
                style={{ outline: 'none' }}
                tabIndex={-1}
              >
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  formatter={(value, name) => [value, categoryLabelMap[name] || name]}
                  shared={false}
                  contentStyle={{ backgroundColor: 'var(--tqa-black)', borderColor: 'var(--tqa-border-dark)' }}
                  itemStyle={{ color: 'var(--tqa-white)' }} labelStyle={{ color: 'var(--tqa-timberwolf)' }}
                />
                {categoryData.map((entry) => (
                  <Bar
                    key={entry.key}
                    dataKey={entry.key}
                    stackId="category"
                    radius={0}
                    fill={entry.color}
                    stroke="none"
                    strokeWidth={0}
                    shape={<BaseBarShape />}
                    activeBar={<ActiveBarShape />}
                    fillOpacity={getSegmentOpacity(activeCategories, entry.key)}
                    onClick={() => onToggleCategory(entry.key)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]" style={{color:'rgba(255,255,255,0.45)'}}>
            {categoryData.map((entry) => (
              <button
                key={entry.key}
                type="button"
                onClick={() => onToggleCategory(entry.key)}
                className="inline-flex items-center gap-1"
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
                {entry.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyzerSummaryBar;
